/**
 * Component: Image and Video
 * ------------------------------------------------------------------------------
 * Video player component to replace an image when ready.
 *
 * @namespace imageVideo
 */
import Player from '@vimeo/player';
import {on} from '~/helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  video: '[js-image-video="video"]',
  image: '[js-image-video="image"]',
  trigger: '[js-image-video="trigger"]',
  youtubeVideo: '[js-video="youtube"]',
};

window.Frame.VideoPlayers = window.Frame.VideoPlayers || {};

/**
 * Create a new example object.
 * @param {object} config - Settings object defined when instantiating.
 */
export default (config) => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    video: [...config.container.querySelectorAll(selectors.video)],
    image: config.container.querySelector(selectors.image),
    trigger: config.container.parentNode.querySelector(selectors.trigger),
    youtubeVideo: config.container.parentNode.querySelector(selectors.youtubeVideo),
  };

  /**
   * Initialise component .
   */
  function init() {
    if (!nodeSelectors.video) { return; }

    nodeSelectors.video.forEach((video) => {
      constructVideoPlayer(video);
    });

    setEventListeners();
    setEventBusListeners();

    if (nodeSelectors.youtubeVideo) {
      constructYoutubePlayer();
    }
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {

    nodeSelectors.video.forEach((video) => {
      const elementId = video.id;
      const player = window.Frame.VideoPlayers[elementId];

      if (!nodeSelectors.trigger) { return; }
      on('click', nodeSelectors.trigger, () => handleToggleControl(player));
    });
  }

  /**
   * Set Event Bus listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('Resize:BreakpointChange', (displayType) => {
      handleBreakpointChange(displayType);
    });
  }

  /**
   * Construct Vimeo video player
   */
  function constructVideoPlayer(video) {
    const vimeoId = video.getAttribute('data-video-id');
    const elementId = video.id;
    const hasControls = video.getAttribute('data-controls');

    if (hasControls === 'true') {
      window.Frame.VideoPlayers[elementId] = new Player(elementId, {
        id: vimeoId,
        controls: 0,
      });
    } else {

      window.Frame.VideoPlayers[elementId] = new Player(elementId, {
        id: vimeoId,
        background: true,
        muted: true,
      });
    }

    window.Frame.VideoPlayers[elementId].ready().then(() => {
      const player = window.Frame.VideoPlayers[elementId];

      if (video.classList.contains('js-no-resize')) {
        handlePlayEvent(player, hasControls);
        return;
      }

      resizeVideo(video, player, hasControls);

    }).catch((error) => {
      window.console.log(error);
    });
  }

  /**
   * Resize video iframe to have cover image size.
   */
  function resizeVideo(video, player, hasControls) {
    const videoIframe = video.querySelector('iframe');
    const customWidth = video.getAttribute('data-custom-width');
    const customHeight = video.getAttribute('data-custom-height');

    Promise.all([
      player.getVideoWidth(),
      player.getVideoHeight(),
      player.getPaused(),
    ]).then(([width, height, isPaused]) => {
      if (!isPaused) {
        setImagePlayerReady();
      }

      const ratioSizes = {
        height: customHeight ? customHeight : calculateHeight(width, height),
        minWidth: customWidth ? customWidth : calculateWidth(width, height),
      };

      Object.assign(videoIframe.style, ratioSizes);

      handlePlayEvent(player, hasControls);
    }).catch((error) => {
      window.console.log(error);
    });
  }

  /**
   * Calculate video height in vw for cover size.
   * - If it's a fullScreen video, check the width of the container in relation
   *   to the window and factor that into the calculations.
   * @param {Number} width  - video original width
   * @param {Number} height - video original height
   * @param {String} - height to pass to the video iframe CSS.
   */
  function calculateHeight(width, height) {
    if (config.container.dataset.size === 'fullScreen') {
      const multiplier = config.container.offsetWidth / window.innerWidth;
      return `${height / width * 100 * multiplier}vw`;
    }

    const unit = (config.container.dataset.size === 'fullScreen') ? 'vw' : '%';
    return `${height / width * 100}${unit}`;
  }

  /**
   * Calculate video min-width in vh for cover size.
   * - If it's a fullScreen video, check the width of the container in relation
   *   to the window and factor that into the calculations.
   * @param {Number} width  - video original width
   * @param {Number} height - video original height
   * @returns {String} - width to pass to video iframe CSS.
   */
  function calculateWidth(width, height) {
    if (config.container.dataset.size === 'fullScreen') {
      const multiplier = config.container.offsetWidth / window.innerWidth;
      return `${width / height * 100 * 100 * multiplier}vh`;
    }

    const unit = (config.container.dataset.size === 'fullScreen') ? 'vh' : '%';
    return `${width / height * 100}${unit}`;
  }

  /**
   * Play video and ide cover image once video is ready.
   */
  function handlePlayEvent(player, hasControls) {
    if (hasControls === 'true') {
      setImagePlayerReady();
      return;
    }

    player.play().then(() => {
      setImagePlayerReady();
    }).catch((error) => {
      window.console.log(error);
    });
  }

  /**
   * Add the `player-ready` class to the image container.
   * - Applies styling to fade out the image once the video is playing.
   */
  function setImagePlayerReady() {
    nodeSelectors.image.classList.add('player-ready');
  }

  /**
   * Initialise video.
   * @param {HTMLElement} control – The clicked control element.
   */
  function handleToggleControl(player) {
    player.getPaused().then((paused) => {
      if (paused) {
        player.play();
        nodeSelectors.trigger.classList.add('is-playing');

      } else {
        player.pause();
        nodeSelectors.trigger.classList.remove('is-playing');
      }

    }).catch((error) => {
      return error;
    });
  }

  /**
   * Handle breakpoint change EventBus event.
   * - If the container is in fullscreen mode, trigger a resize on the video to
   *   make sure it still covers the screen.
   */
  function handleBreakpointChange() {
    const isFullScreenVideo = (config.container.dataset.size === 'fullScreen');
    if (!isFullScreenVideo) { return; }

    nodeSelectors.video.forEach((video) => {
      const hasControls = video.getAttribute('data-controls');
      const player = window.Frame.VideoPlayers[video.id];

      resizeVideo(video, player, hasControls);
    });
  }

  function constructYoutubePlayer() {
    // Inject YouTube API script
    const src = '//www.youtube.com/player_api';
    const scripts = Array.from(document.getElementsByTagName('script'));
    const scriptIncluded = scripts.findIndex((script) => script.src.includes(src));
    if (scriptIncluded === -1) {
      const tag = document.createElement('script');
      tag.src = src;
      const firstScriptTag = document.getElementsByTagName('script')[scripts.length - 1];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
